// Cap on decimals, so a degenerate near-zero price cannot print a string of zeros.
const MAX_DECIMALS = 12

/**
 * Decimals that keep a price readable at its magnitude.
 *
 * A single fixed precision cannot serve the scanned universe: two decimals
 * renders SUI at $0.73 and MATIC at $0.10 (losing the digits that actually
 * move), six renders BTC at $77,420.400000, and four renders SHIB (~$0.0000052)
 * and PEPE (~$0.0000035) as $0.0000 -- entry, stop and target all identical.
 *
 * From $1 up: cents. From $0.01: four decimals. Below that: enough decimals for
 * four significant figures, which keeps an ATR-distance stop and target
 * distinguishable from the entry.
 */
export const priceDecimals = (price: number): number => {
  const magnitude = Math.abs(price)
  if (!Number.isFinite(magnitude) || magnitude === 0 || magnitude >= 1) return 2
  if (magnitude >= 0.01) return 4
  return Math.min(Math.ceil(-Math.log10(magnitude)) + 3, MAX_DECIMALS)
}

/** Market or signal price at a fixed precision for its magnitude (see priceDecimals). */
export const formatPrice = (price: number): string => {
  if (!Number.isFinite(price)) return '—'
  const decimals = priceDecimals(price)
  return price.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * A user-logged trade price. Never shows fewer digits than priceDecimals, but
 * keeps extra precision the user entered (a EUR/USD fill of 1.0885 stays
 * 1.0885, not 1.09) up to eight decimals, which also trims float noise such as
 * 0.30000000000000004.
 */
export const formatTradePrice = (price: number): string => {
  if (!Number.isFinite(price)) return '—'
  return price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(priceDecimals(price), 8),
  })
}

/** Axis tick label: whole numbers from 1,000 up, where cents are noise. */
export const formatAxisPrice = (price: number): string =>
  Math.abs(price) >= 1000 ? price.toLocaleString('en-US', { maximumFractionDigits: 0 }) : formatPrice(price)
